import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { withFormik } from 'formik';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import {
  ascend,
  compose,
  contains,
  defaultTo,
  descend,
  path,
  pick,
  find,
  propEq,
  groupWith,
  sortWith,
  prop,
  last,
  reduce,
  keys,
  filter,
} from 'ramda';
import Select from 'react-select';
import moment from 'moment';
import * as yup from 'yup';

import magic from '../../../../util/magic';
import { handleError, handleSuccess } from '../../../../util/forms';
import { getFileWithKey, createMarkup } from '../../../../util/helpers';
import { apiAddFile, apiSearchCEUCode } from '../../../../api/ceu';
import { routes } from '../../../../routes';
import { isNilOrEmpty } from '../../../../util/redux';

import Card from '../../../../components/Card/index';
import FlaggedUserModal from '../../../../components/Modal/FlaggedUserModal';
import EditField from '../../../../components/Forms/EditField';
import Radio from '../../../../components/Forms/Radio';
import FileBrowser from '../../../../components/Forms/FileBrowser';
import Checkbox from '../../../../components/Forms/Checkbox';

const mapStore = ({ creditTypes }) => ({
  creditTypes,
});

class CEU extends Component {
  static propTypes = {
    admin: PropTypes.bool,
    approve: PropTypes.func,
    reject: PropTypes.func,
    ceu: PropTypes.object,
    creditTypes: PropTypes.array,
    finish: PropTypes.func,
    history: PropTypes.object,
    submit: PropTypes.func,
    userCertifications: PropTypes.array,
  };

  static defaultProps = {
    ceu: {},
  };

  updateableFields = ['credits', 'creditTypeId', 'programTitle', 'sponsor', 'programAgenda'];

  StaticEditField = (props) => {
    const { ceu, values, admin } = this.props;
    const staticValue = defaultTo(props.value, props.static);

    return (ceu.approved && !admin) ||
      (values.preapprovedCreditId &&
        contains(props.name, this.updateableFields) &&
        !contains(props.name, values.preapprovedEmptyFields)) ||
      props.isStatic ? (
      !isNilOrEmpty(staticValue) && (
        <div className="form-group row">
          <label className="col-form-label col-md-3">{props.label}</label>
          <div className="col-md-7 d-flex align-items-center">{staticValue}</div>
        </div>
      )
    ) : (
      <EditField {...props} />
    );
  };

  renderReviewsTable = () => {
    const { ceu } = this.props;
    const reviews = defaultTo([], ceu.reviews);

    if (reviews.length == 0) return <noscript />;

    return (
      <div className="row">
        <div className="col-md-10">
          <h4 className="bold-blue-heading mt-5 mb-3">CEU Reviews</h4>
          <table className="table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Reviewer</th>
                <th>Message</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {reviews.map((review) => (
                <tr key={review.id}>
                  <td>{moment(review.createdAt).format('MM/DD/YYYY')}</td>
                  <td>
                    {path(['user', 'firstName'], review)} {path(['user', 'lastName'], review)}
                  </td>
                  <td dangerouslySetInnerHTML={createMarkup(review.message)} />
                  <td>{path(['extra', 'status'], review)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  renderButtonsRow = () => {
    const { approve, reject, handleSubmit, isSubmitting, isValid, ceu } = this.props;

    return (
      <div className="form-group row row mb-0">
        <div className="col-md-10 text-right">
          {reject && (
            <button type="button" className="btn btn-outline-danger mr-2" onClick={reject}>
              Reject
            </button>
          )}
          {approve && (
            <button type="button" className="btn btn-secondary mr-2" onClick={approve}>
              Approve
            </button>
          )}
          <button type="submit" className="btn btn-primary" disabled={!isValid || isSubmitting} onClick={handleSubmit}>
            {ceu.approved === false && ceu.reviews.length ? 'Resubmit' : 'Save Changes'}
          </button>
        </div>
      </div>
    );
  };

  render() {
    const {
      admin,
      ceu,
      creditTypes,
      values,
      handleBlur,
      handleChange,
      setValues,
      status,
      setFieldValue,
      userCertifications,
      errors,
      user,
    } = this.props;
    const { StaticEditField } = this;
    const firstName = path(['user', 'firstName'], ceu);
    const lastName = path(['user', 'lastName'], ceu);
    const selectedCreditType = defaultTo({}, find(propEq('id', values.creditTypeId), creditTypes));
    const userCertificationGroups = compose(
      groupWith((a, b) => a.reportingPeriodId == b.reportingPeriodId),
      filter((cert) => !moment(path(['reportingPeriod', 'start'], cert)).isAfter(moment())),
      sortWith([descend(prop('reportingPeriodId')), descend(prop('status')), ascend(prop('createdAt'))]),
    )(userCertifications);

    return (
      <div>
        {admin && (
          <FlaggedUserModal
            user={ceu.user}
          >
          </FlaggedUserModal>
        )}
        <Card className="container">
          <Card.Header compact>Continuing Education (CEU) Individual Approval</Card.Header>
          <Card.Body>
            <form className="offset-md-1">
              {admin && (
                <StaticEditField
                isStatic
                label="Name"
                static={
                  <Link to={routes.admin.users.profileBuilder(path(['user', 'id'], ceu))}>
                      {firstName} {lastName}
                    </Link>
                  }
                  />
                  )}
              {admin && values.preapprovedCreditId && (
                <StaticEditField
                isStatic
                label="Group CEU"
                static={<Link to={routes.admin.groupCeus.reviewBuilder(values.preapprovedCreditId)}>View</Link>}
                />
                )}
              <StaticEditField
                name="completionCode"
                label="Completion Code Phrase"
                status={status}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.completionCode}
                tip="If you were provided a code phrase, please enter it here exactly as provided, and click the blue 'Apply' button."
                >
                <button
                  type="button"
                  className="btn btn-primary btn-padded btn-not-round"
                  onClick={() =>
                    apiSearchCEUCode(values.completionCode)
                    .then(({ data }) => {
                      setValues({
                        ...values,
                        ...pick(this.updateableFields, data),
                        preapprovedEmptyFields: keys(filter(isNilOrEmpty, data)),
                        preapprovedCreditId: data.id,
                      });
                      handleSuccess({ props: this.props, ...this.props }, 'Applied Code Phrase');
                    })
                    .catch((err) => handleError({ props: this.props, ...this.props }, err))
                  }
                  >
                  Apply
                </button>
              </StaticEditField>
              <EditField
                as={Select}
                bare
                simpleValue
                name="reportingPeriodId"
                label="Reporting Period"
                status={status}
                error={errors.reportingPeriodId}
                onChange={(value) => setFieldValue('reportingPeriodId', value)}
                options={userCertificationGroups.map((group) => {
                  const cert = last(group);
                  return {
                    value: cert.reportingPeriodId,
                    label: `${path(['reportingPeriod', 'name'], cert)} (${path(['status'], cert)})`,
                  };
                })}
                value={values.reportingPeriodId}
                />
              <EditField
                name="completionDate"
                label="Completion Date"
                type="date"
                status={status}
                error={errors.completionDate}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.completionDate}
                />
              <StaticEditField
                type="number"
                name="credits"
                label="CEUs Requested"
                required
                status={status}
                error={errors.credits}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.credits}
                />
              <StaticEditField
                as={Select}
                bare
                simpleValue
                name="creditTypeId"
                label="Credit Type"
                status={status}
                error={errors.creditTypeId}
                onChange={(value) => setFieldValue('creditTypeId', value)}
                options={creditTypes.map((type) => ({ value: type.id, label: type.name }))}
                value={values.creditTypeId}
                static={selectedCreditType.name}
                />
              <StaticEditField
                name="programTitle"
                label="Program Title"
                required
                status={status}
                error={errors.programTitle}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.programTitle}
                />
              <StaticEditField
                name="sponsor"
                label="Sponsor"
                status={status}
                error={errors.sponsor}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.sponsor}
                />
              <StaticEditField
                as="textarea"
                name="programDescription"
                label="How does this program impact and enhance the work you are doing as an Sage Financial Solutions Certified Professional"
                status={status}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.programDescription}
                error={errors.programDescription}
                />
              {values.preapprovedCreditId && !contains('programAgenda', values.preapprovedEmptyFields) ? (
                <StaticEditField
                as="textarea"
                label="Program Agenda"
                name="programAgenda"
                status={status}
                error={errors.programAgenda}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.programAgenda}
                />
                ) : (
                  <Fragment>
                  <div className="form-group row mb-0">
                    <label className="col-md-3 col-form-label">Program Agenda</label>
                    <div className="col-md-7">
                      <Radio
                        inline
                        name="agendaMethod"
                        label="Upload Agenda"
                        value="upload"
                        checked={values.agendaMethod}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        />
                      <Radio
                        inline
                        name="agendaMethod"
                        label="Type into text box"
                        value="type"
                        checked={values.agendaMethod}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        />
                    </div>
                  </div>
                  {values.agendaMethod == 'upload' && (
                    <EditField
                    as={FileBrowser}
                    name="programAgendaFile"
                    label="&nbsp;"
                    error={errors.fakeMedia}
                    files={values.media}
                    fileKey={magic.programAgenda}
                    addFile={(file) => apiAddFile(ceu.id, file)}
                    update={(media) => setFieldValue('media', media)}
                    fake={isNilOrEmpty(ceu.id)}
                    fakeFiles={values.fakeMedia}
                    fakeUpdate={({ files, fakeFiles }) => setValues({ ...values, media: files, fakeMedia: fakeFiles })}
                    />
                    )}
                  {values.agendaMethod == 'type' && (
                    <EditField
                    as="textarea"
                    label="&nbsp;"
                    name="programAgenda"
                    status={status}
                    error={errors.programAgenda}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.programAgenda}
                    />
                    )}
                  {!values.preapprovedCreditId && (
                    <EditField
                    as={FileBrowser}
                    name="completionFile"
                    label="Upload Certificate of Completion / Participant Program Schedule"
                    files={values.media}
                    fileKey={magic.creditCompletion}
                    addFile={(file) => apiAddFile(ceu.id, file)}
                    update={(media) => setFieldValue('media', media)}
                    fake={isNilOrEmpty(ceu.id)}
                    fakeFiles={values.fakeMedia}
                    fakeUpdate={({ files, fakeFiles }) => setValues({ ...values, media: files, fakeMedia: fakeFiles })}
                    />
                    )}
                </Fragment>
              )}
              {!admin && (
                <Checkbox
                name="verified"
                label="I verify that the information on this request is true and accurate."
                checked={values.verified}
                onBlur={handleBlur}
                onChange={handleChange}
                error={errors.verified}
                />
                )}
              {this.renderReviewsTable()}
              {this.renderButtonsRow()}
            </form>
          </Card.Body>
        </Card>
      </div>
    );
  }
}

export default compose(
  withRouter,
  withFormik({
    handleSubmit: (payload, formProps) => {
      const { props } = formProps;

      props
        .submit(payload)
        .then((resp) => {
          handleSuccess(formProps);
          return resp;
        })
        .then((resp) => props.finish(resp))
        .catch((err) => handleError(formProps, err));
    },
    mapPropsToValues: ({ ceu = {}, userCertifications }) => {
      const latestReportingPeriod = reduce(
        (a, cert) =>
          a < cert.reportingPeriodId && !moment(path(['reportingPeriod', 'start'], cert)).isAfter(moment())
            ? cert.reportingPeriodId
            : a,
        null,
        userCertifications,
      );

      return {
        id: ceu.id,
        preapprovedCreditId: defaultTo('', ceu.preapprovedCreditId),
        preapprovedEmptyFields: [],
        credits: defaultTo('', ceu.credits),
        reportingPeriodId: defaultTo('', defaultTo(latestReportingPeriod, ceu.reportingPeriodId)),
        creditTypeId: defaultTo('', ceu.creditTypeId),
        programTitle: defaultTo('', ceu.programTitle),
        sponsor: defaultTo('', ceu.sponsor),
        programDescription: defaultTo('', ceu.programDescription),
        agendaMethod: defaultTo('upload', ceu.programAgenda ? 'type' : 'upload'),
        programAgenda: defaultTo('', ceu.programAgenda),
        completionMethod: defaultTo('upload', ceu.completionCode ? 'code' : 'upload'),
        completionCode: defaultTo('', ceu.completionCode),
        completionDate: defaultTo(moment().format('YYYY-MM-DD'), ceu.completionDate),
        media: defaultTo([], ceu.media),
        verified: ceu.id != undefined,
        fakeMedia: [],
      };
    },
    validationSchema: ({ admin }) =>
      yup.object().shape(
        {
          reportingPeriodId: yup.number().required('This field is required'),
          completionDate: yup.string().required('This field is required'),
          credits: yup.number().required('This field is required'),
          creditTypeId: yup.number().required('This field is required'),
          programTitle: yup.string().required('This field is required'),
          sponsor: yup.string().required('This field is required'),
          programDescription: yup.string().required('This field is required'),
          programAgenda: yup.string().when(['fakeMedia', 'media'], (fakeMedia, media, schema) => {
            return isNilOrEmpty(getFileWithKey(fakeMedia, magic.programAgenda)) &&
              isNilOrEmpty(getFileWithKey(media, magic.programAgenda))
              ? schema.required('This field is required').nullable()
              : schema.nullable();
          }),
          fakeMedia: yup.mixed().when(['programAgenda', 'media'], (programAgenda, media, schema) => {
            return isNilOrEmpty(programAgenda) && isNilOrEmpty(getFileWithKey(media, magic.programAgenda))
              ? schema.test(
                  'fakeMedia',
                  'This field is required',
                  (value) => !isNilOrEmpty(getFileWithKey(value, magic.programAgenda)),
                )
              : schema;
          }),
          verified: admin ? null : yup.boolean().equals([true], 'This field is required'),
        },
        ['fakeMedia', 'programAgenda'],
      ),
    isInitialValid: ({ initialValues, validationSchema }) => validationSchema().isValidSync(initialValues),
    validateOnChange: true,
  }),
  connect(mapStore),
)(CEU);
